import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  StandardizedTable,
  TableColumn,
} from "@/components/ui/standardized-table";
import {
  Plus,
  Edit,
  Building2,
  Loader2,
  AlertTriangle,
  CheckCircle,
  Ban,
  X,
} from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/auth.context";
import { useAccount } from "@/contexts/account.context";
import {
  createAccountApiV1AccountsPost,
  deleteAccountPermanentlyApiV1AccountsAccountIdDelete,
  listAccountsApiV1AccountsGet,
  listSegmentsApiV1SegmentsGet,
  updateAccountApiV1AccountsAccountIdPut,
  updateAccountStatusApiV1AccountsAccountIdStatusPatch,
} from "@/backend-sdk/client/sdk.gen";
import type {
  ManagedAccountListItem,
  SegmentItem,
} from "@/backend-sdk/client/types.gen";
import { heyAdminApiClient, isHeyApiError } from "@/lib/hey-api-client";

type Account = ManagedAccountListItem;

const getErrorDescription = (error: unknown, fallback: string) => {
  if (isHeyApiError(error)) {
    if (typeof error.detail === "string") return error.detail;
    if (
      error.detail &&
      typeof error.detail === "object" &&
      "message" in error.detail
    ) {
      const message = (error.detail as { message?: unknown }).message;
      if (typeof message === "string") return message;
    }
  }
  return fallback;
};

/**
 * <M-279> O teto de lojas do plano, dito do jeito que a tela fala. Serve ao aviso da página, ao
 * clique em "Nova Conta" e à recusa do `POST`, que chega como `Limite de contas atingido (N)`.
 */
const LIMITE_ATINGIDO = "Não é possível adicionar mais lojas";
const descricaoDoLimite = (limite: number) =>
  `Seu plano permite até ${limite} ${limite === 1 ? "loja" : "lojas"}. Para cadastrar outra, faça upgrade do plano em Minha Conta.`;
const limiteDaRecusa = (descricao: string): number | null => {
  const achado = /^Limite de contas atingido \((\d+)\)$/.exec(descricao);
  return achado ? Number(achado[1]) : null;
};

const AccountsPage = () => {
  const { user } = useAuth();
  const { loadAccounts: reloadContextAccounts, isAgencyUser } = useAccount();
  const [loading, setLoading] = useState(false);
  const [accounts, setAccounts] = useState<Account[]>([]);
  // Separado de `loading`, que é dos botões dos diálogos: este diz se a lista ainda não
  // chegou, e começa ligado para a tela não abrir em "Nenhuma conta cadastrada".
  const [carregandoContas, setCarregandoContas] = useState(true);
  // <M-279> `null` é sem limite — ou limite ainda não conhecido, e aí quem confere é o `POST`.
  const [maxAccounts, setMaxAccounts] = useState<number | null>(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);
  const [segments, setSegments] = useState<SegmentItem[]>([]);

  // <D-062> Sem `password` e sem `email`: a criação da loja não cria mais usuário nenhum, e
  // os dois campos só existiam para formar o login daquela credencial invisível. Nenhum
  // e-mail do sistema saía pelo da loja, então exigi-lo só fazia a agência inventar um dado.
  // Efeito colateral que era o problema todo: o e-mail do lojista deixou de ficar ocupado, e
  // essa mesma pessoa pode ser cadastrada como usuário do tipo Cliente.
  const [formData, setFormData] = useState({
    name: "",
    segment: "",
  });

  // Só mexe no carregamento da lista: `loading` é dos botões dos diálogos, e as mutações que
  // chamam esta função ainda estão em curso quando ela termina.
  const loadAccounts = async () => {
    setCarregandoContas(true);
    try {
      const { data: response } = await listAccountsApiV1AccountsGet({
        client: heyAdminApiClient,
        throwOnError: true,
      });
      setAccounts(response.accounts || []);
      setMaxAccounts(response.max_accounts ?? null);
    } catch (error) {
      toast({
        title: "Erro ao carregar contas",
        description: getErrorDescription(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setCarregandoContas(false);
    }
  };

  // `accounts` é o mesmo universo que o `POST` conta: todas as lojas do assinante, de
  // qualquer status.
  const limiteAtingido = maxAccounts !== null && accounts.length >= maxAccounts;

  const abrirCadastro = () => {
    if (limiteAtingido) {
      toast({
        title: LIMITE_ATINGIDO,
        description: descricaoDoLimite(maxAccounts),
        variant: "destructive",
      });
      return;
    }
    setShowAddDialog(true);
  };

  const loadSegments = async () => {
    try {
      const { data } = await listSegmentsApiV1SegmentsGet();
      setSegments(data?.segments ?? []);
    } catch {
      setSegments([]);
    }
  };

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    loadAccounts();
    loadSegments();
  }, []);

  // A loja sem segmento fica fora do benchmark, e o backend recusa criá-la ou apagar o
  // segmento dela. O aviso aqui só poupa a ida ao servidor.
  const segmentoAusente = () => {
    if (formData.segment) return false;
    toast({
      title: "Campo obrigatório",
      description: "Selecione o segmento da loja.",
      variant: "destructive",
    });
    return true;
  };

  const handleAddAccount = async () => {
    if (!formData.name.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "O nome da conta é obrigatório.",
        variant: "destructive",
      });
      return;
    }
    if (segmentoAusente()) return;

    setLoading(true);
    try {
      await createAccountApiV1AccountsPost({
        body: {
          name: formData.name,
          document: null,
          person_type: "fisica",
          phone: null,
          // Obrigatório: é por ele que a loja entra no benchmark.
          segment: formData.segment,
        },
        client: heyAdminApiClient,
        throwOnError: true,
      });

      toast({
        title: "Conta criada com sucesso!",
        description: isAgencyUser
          ? "A nova conta foi adicionada à sua agência."
          : "A nova conta foi adicionada.",
      });

      setShowAddDialog(false);
      resetForm();
      await loadAccounts();
      await reloadContextAccounts();
    } catch (error) {
      console.error(error);

      const descricao = getErrorDescription(error, "Tente novamente.");
      const limite = limiteDaRecusa(descricao);
      if (limite !== null) {
        setShowAddDialog(false);
        resetForm();
      }
      // A lista da tela pode estar velha — uma loja criada em outra aba —, e é dela, com o
      // `max_accounts` que vem junto, que o aviso do teto se deriva. Recarregar em toda recusa
      // não depende de o texto do `400` casar com `limiteDaRecusa`.
      await loadAccounts();
      toast({
        title: limite !== null ? LIMITE_ATINGIDO : "Erro ao criar conta",
        description: limite !== null ? descricaoDoLimite(limite) : descricao,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditAccount = async () => {
    if (!selectedAccount || !formData.name.trim()) {
      toast({
        title: "Campo obrigatório",
        description: "O nome da conta é obrigatório.",
        variant: "destructive",
      });
      return;
    }
    if (segmentoAusente()) return;

    setLoading(true);
    try {
      await updateAccountApiV1AccountsAccountIdPut({
        path: { account_id: selectedAccount.id },
        body: {
          name: formData.name,
          segment: formData.segment,
        },
        client: heyAdminApiClient,
        throwOnError: true,
      });

      toast({
        title: "Conta atualizada com sucesso!",
        description: "As informações da conta foram atualizadas.",
      });

      setShowEditDialog(false);
      setSelectedAccount(null);
      resetForm();
      await loadAccounts();
      await reloadContextAccounts();
    } catch (error) {
      toast({
        title: "Erro ao atualizar conta",
        description: getErrorDescription(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (account: Account) => {
    setSelectedAccount(account);
    setShowConfirmDialog(true);
  };

  const handleDeleteAccount = async (account: Account) => {
    setSelectedAccount(account);
    setShowDeleteDialog(true);
  };

  const confirmStatusChange = async () => {
    if (!selectedAccount) return;

    setLoading(true);
    setShowConfirmDialog(false);

    try {
      const newStatus =
        selectedAccount.status === "active" ? "inactive" : "active";
      await updateAccountStatusApiV1AccountsAccountIdStatusPatch({
        path: { account_id: selectedAccount.id },
        body: { status: newStatus },
        client: heyAdminApiClient,
        throwOnError: true,
      });

      toast({
        title: `Conta ${newStatus === "active" ? "ativada" : "inativada"} com sucesso!`,
        description: `A conta "${selectedAccount.name}" foi ${newStatus === "active" ? "ativada" : "inativada"}.`,
      });

      await loadAccounts();
      await reloadContextAccounts();
    } catch (error) {
      toast({
        title: "Erro ao alterar status da conta",
        description: getErrorDescription(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setSelectedAccount(null);
    }
  };

  const confirmDeleteAccount = async () => {
    if (!selectedAccount) return;

    setLoading(true);
    setShowDeleteDialog(false);

    try {
      await deleteAccountPermanentlyApiV1AccountsAccountIdDelete({
        path: { account_id: selectedAccount.id },
        client: heyAdminApiClient,
        throwOnError: true,
      });

      toast({
        title: "Conta excluída com sucesso!",
        description: `A conta "${selectedAccount.name}" e todos os seus dados foram removidos permanentemente.`,
      });

      await loadAccounts();
      await reloadContextAccounts();
    } catch (error) {
      toast({
        title: "Erro ao excluir conta",
        description: getErrorDescription(error, "Tente novamente."),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setSelectedAccount(null);
    }
  };

  const openEditDialog = (account: Account) => {
    setSelectedAccount(account);
    setFormData({
      name: account.name,
      segment: account.segment || "",
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    setFormData({
      name: "",
      segment: "",
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-100 text-green-800">Ativa</Badge>;
      case "inactive":
        return (
          <Badge variant="secondary" className="bg-gray-100 text-gray-800">
            Inativa
          </Badge>
        );
      case "suspended":
        return <Badge variant="destructive">Suspensa</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const accountsColumns: TableColumn[] = [
    {
      key: "name",
      header: "Nome",
      type: "text",
      render: (value) => (
        <div className="flex items-center gap-2 min-w-0">
          <Building2 className="h-4 w-4 text-[#2869C1] shrink-0" />
          <span className="block truncate" title={String(value ?? "")}>
            {String(value ?? "")}
          </span>
        </div>
      ),
    },
    {
      key: "segment",
      header: "Segmento",
      type: "text",
      render: (value) => String(value ?? "Não informado"),
    },
    {
      key: "status",
      header: "Status",
      type: "text",
      render: (value) => getStatusBadge(String(value ?? "")),
    },
    {
      key: "_actions",
      header: "Ações",
      type: "text",
      render: (_value, row) => {
        const account = row as Account;
        return (
          <div className="flex items-center gap-2 justify-end">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => openEditDialog(account)}
              title="Editar conta"
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleStatusChange(account)}
              className={
                account.status === "active"
                  ? "text-orange-600 hover:text-orange-700"
                  : "text-green-600 hover:text-green-700"
              }
              title={
                account.status === "active" ? "Inativar conta" : "Ativar conta"
              }
            >
              {account.status === "active" ? (
                <Ban className="h-4 w-4" />
              ) : (
                <CheckCircle className="h-4 w-4" />
              )}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => handleDeleteAccount(account)}
              className="text-red-600 hover:text-red-700"
              title="Excluir conta permanentemente"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ];

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isAgencyUser ? "Cadastro de Contas da Agência" : "Minhas Contas"}
          </h1>
          <p className="text-muted-foreground">
            {isAgencyUser
              ? "Gerencie as contas dos seus clientes e estabelecimentos"
              : "Gerencie as lojas da sua conta"}
          </p>
        </div>
        <Button
          onClick={abrirCadastro}
          className="bg-[#2869C1] hover:bg-[#2869C1]/90"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      {limiteAtingido && (
        <div
          role="status"
          className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4"
        >
          <AlertTriangle className="h-5 w-5 text-amber-500 shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-900">
              {LIMITE_ATINGIDO}
            </p>
            <p className="text-sm text-amber-800">
              {descricaoDoLimite(maxAccounts)}
            </p>
          </div>
        </div>
      )}

      {!carregandoContas &&
      (!Array.isArray(accounts) || accounts.length === 0) ? (
        <Card>
          <CardHeader>
            <CardTitle
              className="flex items-center gap-2"
              icon={<Building2 className="h-5 w-5" />}
              subtitle="Lista de todas as contas vinculadas à sua assinatura"
            >
              Contas Cadastradas
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-12">
              <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Nenhuma conta cadastrada
              </h3>
              <p className="text-gray-500 mb-4">
                {isAgencyUser
                  ? "Comece adicionando uma conta para seus clientes"
                  : "Comece adicionando a sua primeira loja"}
              </p>
              <Button
                onClick={abrirCadastro}
                className="bg-[#2869C1] hover:bg-[#2869C1]/90"
              >
                <Plus className="h-4 w-4 mr-2" />
                Adicionar Primeira Conta
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <StandardizedTable
          title="Contas Cadastradas"
          description="Lista de todas as contas vinculadas à sua assinatura"
          columns={accountsColumns}
          data={accounts}
          loading={carregandoContas}
        />
      )}

      {/* Dialog para adicionar conta */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Adicionar Nova Conta</DialogTitle>
            <DialogDescription>
              {isAgencyUser
                ? "Preencha as informações da nova conta que será vinculada à sua agência"
                : "Preencha as informações da nova conta"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome da Conta *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome do estabelecimento"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="segment">Segmentação *</Label>
              <Select
                value={formData.segment}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, segment: value }))
                }
              >
                <SelectTrigger id="segment">
                  <SelectValue placeholder="Selecione o segmento do e-commerce" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {segments.map((s) => (
                    <SelectItem key={s.slug} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleAddAccount} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Criar Conta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog para editar conta */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Editar Conta</DialogTitle>
            <DialogDescription>
              Atualize as informações da conta
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit_name">Nome da Conta *</Label>
              <Input
                id="edit_name"
                value={formData.name}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, name: e.target.value }))
                }
                placeholder="Nome do estabelecimento"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit_segment">Segmentação *</Label>
              <Select
                value={formData.segment}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, segment: value }))
                }
              >
                <SelectTrigger id="edit_segment">
                  <SelectValue placeholder="Selecione o segmento do e-commerce" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {segments.map((s) => (
                    <SelectItem key={s.slug} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancelar
            </Button>
            <Button onClick={handleEditAccount} disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Salvar Alterações
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação para inativação/ativação */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div
                className={`p-2 rounded-full ${selectedAccount?.status === "active" ? "bg-orange-100" : "bg-green-100"}`}
              >
                {selectedAccount?.status === "active" ? (
                  <Ban className="h-5 w-5 text-orange-600" />
                ) : (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                )}
              </div>
              <div>
                <DialogTitle className="text-lg">
                  {selectedAccount?.status === "active"
                    ? "Inativar Conta"
                    : "Ativar Conta"}
                </DialogTitle>
                <DialogDescription>
                  {selectedAccount?.status === "active"
                    ? "Esta ação irá desabilitar o acesso desta conta ao sistema."
                    : "Esta ação irá restabelecer o acesso desta conta ao sistema."}
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-muted/50 p-3 rounded-lg">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <span className="font-medium">{selectedAccount?.name}</span>
              </div>
              {selectedAccount?.status === "active" && (
                <p className="text-sm text-muted-foreground mt-1">
                  A conta ficará inativa e não poderá mais acessar o sistema até
                  ser reativada.
                </p>
              )}
              {selectedAccount?.status === "inactive" && (
                <p className="text-sm text-muted-foreground mt-1">
                  A conta será reativada e poderá acessar o sistema normalmente.
                </p>
              )}
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowConfirmDialog(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmStatusChange}
              disabled={loading}
              className={
                selectedAccount?.status === "active"
                  ? "bg-orange-600 hover:bg-orange-700"
                  : "bg-green-600 hover:bg-green-700"
              }
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {selectedAccount?.status === "active" ? "Inativar" : "Ativar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmação para exclusão permanente */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <DialogTitle className="text-lg">
                  Excluir Conta Permanentemente
                </DialogTitle>
                <DialogDescription>
                  Esta ação irá remover permanentemente a conta e todos os dados
                  dela. Nenhum usuário é excluído.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="py-4">
            <div className="bg-red-50 border border-red-200 p-3 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Building2 className="h-4 w-4 text-red-600" />
                <span className="font-medium text-red-800">
                  {selectedAccount?.name}
                </span>
              </div>
              <div className="text-sm text-red-700 space-y-1">
                <p>
                  ⚠️ <strong>Atenção:</strong> Esta ação é irreversível!
                </p>
                <p>
                  • A conta, as integrações e as métricas serão excluídas
                  permanentemente
                </p>
                <p>
                  • Quem tinha acesso a ela continua com o login ativo e com as
                  outras contas a que estiver vinculado
                </p>
                <p>
                  • Para remover uma pessoa, use o menu Minha Conta, aba
                  Usuários — se esta for a única conta de um cliente, remova-o
                  antes de excluí-la
                </p>
                <p>• Não será possível recuperar as informações</p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDeleteDialog(false)}
              disabled={loading}
            >
              Cancelar
            </Button>
            <Button
              onClick={confirmDeleteAccount}
              disabled={loading}
              className="bg-red-600 hover:bg-red-700"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Excluir Permanentemente
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AccountsPage;
